import React, { useState, useEffect, useRef } from 'react'
import { SongManager } from '../services/SongManager'
import exampleLyrics from '../assets/exampleLyrics.txt?raw'
import songbook from '../assets/songbook.json'
import { downloadTextFile, readTextFile } from '../utils/fileUtils'

type SongbookSong = {
	title: string;
	number: number;
	number_letter: string | null;
	structure: {
		type: string;
		content: string;
		verseNum?: number | undefined
	}[];
	instructions: string[];
	section: string;
}

interface SongInfo {
	id: string;
	title: string;
	lastModified: number;
}

interface SongPickerProps {
	isOpen: boolean;
	onClose: () => void;
	onSelectSong: (songId: string) => void;
	currentSongId?: string;
}

export const SongPicker: React.FC<SongPickerProps> = ({
	isOpen,
	onClose,
	onSelectSong,
	currentSongId,
}) => {
	const [songs, setSongs] = useState<SongInfo[]>([])
	const [searchFilter, setSearchFilter] = useState('')
	const [newSongTitle, setNewSongTitle] = useState('')
	const fileInputRef = useRef<HTMLInputElement>(null)
	const searchFilterLc = searchFilter.toLowerCase()

	// Filter saved songs based on search
	const filteredSongs = songs.filter(song =>
		song.title.toLowerCase().includes(searchFilterLc),
	)

	// Filter songbook songs based on search
	const filteredSongbookSongs: SongbookSong[] = searchFilter ? songbook.filter(song => {
		return String(song.number) === searchFilter || song.title.toLowerCase().includes(searchFilterLc)
	}) : []

	useEffect(() => {
		if (isOpen) {
			const songList = SongManager.getSongList()

			// If no songs exist, create an example song
			if (songList.length === 0) {
				const exampleSong = SongManager.createSongFromLyrics(exampleLyrics, 'Example Song (start here)')
				SongManager.saveSong(exampleSong)

				// Refresh the song list to include the new example song
				const updatedSongList = SongManager.getSongList()
				setSongs(updatedSongList.sort((a, b) => b.lastModified - a.lastModified))
			} else {
				setSongs(songList.sort((a, b) => b.lastModified - a.lastModified))
			}
		}
	}, [isOpen])

	const handleCreateNewSong = () => {
		const title = newSongTitle.trim() || 'New Song'
		const songId = SongManager.generateSongId()
		const newSong = SongManager.createSongFromLyrics('', title)
		newSong.id = songId

		SongManager.saveSong(newSong)
		onSelectSong(songId)
		setNewSongTitle('')
		onClose()
	}

	const handleDeleteSong = (songId: string, event: React.MouseEvent) => {
		event.stopPropagation()
		if (confirm('Are you sure you want to delete this song?')) {
			SongManager.deleteSong(songId)
			setSongs(prev => prev.filter(song => song.id !== songId))

			// If we deleted the current song, clear the URL
			if (songId === currentSongId) {
				window.history.pushState({}, '', window.location.pathname)
			}
		}
	}

	const handleExportSong = (songId: string, event: React.MouseEvent) => {
		event.stopPropagation()
		const song = SongManager.loadSong(songId)
		if (song) {
			const content = song.lyrics
			const filename = `${song.title}.txt`
			downloadTextFile(content, filename)
		}
	}

	const handleImportSong = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (file) {
			const content = await readTextFile(file)
			const title = file.name.replace(/\.txt$/, '')
			const newSong = SongManager.createSongFromLyrics(content, title)
			SongManager.saveSong(newSong)
			setSongs(SongManager.getSongList().sort((a, b) => b.lastModified - a.lastModified))
		}
	}

	const formatDate = (timestamp: number) => {
		return new Date(timestamp).toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		})
	}

	const importFromSongbook = (song: SongbookSong) => {
		const lyrics = song.structure
			.map(section => section.type !== 'unknown' ? section : null)
			.filter(Boolean)
			.map(section => section!.content).join('\n')

		const newSong = SongManager.createSongFromLyrics(lyrics, song.title)
		SongManager.saveSong(newSong)
		setSongs(SongManager.getSongList().sort((a, b) => b.lastModified - a.lastModified))
	}

	if (!isOpen) return null

	return (
		<div className="modal-overlay" onClick={onClose}>
			<div className="modal-content" onClick={(e) => e.stopPropagation()}>
				<div className="modal-header">
					<h2>Select a Song</h2>
					{/* <button className="modal-close" onClick={onClose}>×</button> */}
				</div>

				<div className="modal-body">
					<div className="new-song-section">
						<div className="new-song-input">
							<input
								type="text"
								placeholder="New song title..."
								value={newSongTitle}
								onChange={(e) => setNewSongTitle(e.target.value)}
								onKeyDown={(e) => e.key === 'Enter' && handleCreateNewSong()}
							/>
							<button onClick={handleCreateNewSong} className="btn btn-primary">
								Create New Song
							</button>
							<button onClick={() => fileInputRef.current?.click()} className="btn">
								Import
							</button>
							<input
								type="file"
								ref={fileInputRef}
								style={{ display: 'none' }}
								accept=".txt"
								onChange={handleImportSong}
							/>
						</div>
					</div>

					<div className="search-section">
						<input
							type="text"
							placeholder="Search songs..."
							value={searchFilter}
							onChange={(e) => setSearchFilter(e.target.value)}
							className="search-input"
							autoFocus
						/>
					</div>

					<div className="songs-list">
						{filteredSongs.length === 0 && searchFilter ? (
							<div className="empty-state">
								<p>No saved songs match "{searchFilter}"</p>
							</div>
						) : filteredSongs.length === 0 ? (
							<div className="empty-state">
								<p>No songs saved yet. Create your first song above!</p>
							</div>
						) : (
							<>
								<h3 className="section-header">Your Songs</h3>
								{filteredSongs.map((song) => (
									<div
										key={song.id}
										className={`song-item ${song.id === currentSongId ? 'song-item--current' : ''}`}
										onClick={() => {
											onSelectSong(song.id)
											onClose()
										}}
									>
										<div className="song-info">
											<h4 className="song-title">{song.title}</h4>
											<p className="song-date">Last modified: {formatDate(song.lastModified)}</p>
										</div>
										<button
											className="song-action-btn"
											onClick={(e) => handleExportSong(song.id, e)}
											title="Export song"
										>
											Export
										</button>
										<button
											className="song-action-btn"
											onClick={(e) => handleDeleteSong(song.id, e)}
											title="Delete song"
										>
											Delete
										</button>
									</div>
								))}
							</>
						)}
					</div>

					{filteredSongbookSongs.length > 0 && (
						<div className="songbook-section">
							<h3 className="section-header">Primary Songbook</h3>
							<div className="songbook-list">
								{filteredSongbookSongs.map(song => (
									<div key={`${song.number}${song.number_letter || ''}`} className="songbook-item">
										<div className="songbook-info">
											<span className="song-number">{song.number}{song.number_letter || ''}</span>
											<span className="song-title">{song.title}</span>
										</div>
										<button 
											onClick={() => importFromSongbook(song)}
											className="btn btn-secondary btn-small"
										>
											Import
										</button>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}
